import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import * as DocumentPicker from 'expo-document-picker';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { evidenceApi } from '../../services/apiService';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { getTextAlign } from '../../utils/rtl';
import { tokens } from '@design';
import { Button } from '@design/components/Button';

type RouteProps = RouteProp<MainStackParamList, 'AddEvidence'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icons
// ============================================================

const InfoIcon = ({ size = 16, color = tokens.colors.raw.gray['500'] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke={color}
      strokeWidth="1.5"
    />
    <Path d="M12 16V12M12 8H12.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </Svg>
);

// ============================================================
// Constants
// ============================================================

const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

// ============================================================
// Main Component
// ============================================================

export default function AddEvidenceScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { evidenceId, relatedType, relatedId, contextLabel } = route.params;
  const isEditMode = !!evidenceId;

  const { user } = useAuth();
  // Evidence writes are role-gated only — NOT gated by the department's
  // data_entry_open flag. Evidence follows the KPI-reading model (readings stay
  // enterable after close; the grace window flags a late reading rather than
  // blocking it), so `isViewOnly` must NOT be used here: it folds in
  // `org_employee && !dataEntryOpen` and would bounce a legitimate uploader
  // back out of this screen. Mirrors EvidenceAccessPermission on the backend
  // and `canManageEvidence` on the 13 screens that host EvidenceTable.
  const canManageEvidence = ['org_employee', 'coordinator', 'superuser'].includes(
    user?.profile?.user_type || ''
  );
  const { showSuccess, showError } = useNotification();
  const { colors } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [form, setForm] = useState({
    title: '',
    description: '',
    target_criterion: '',
    impact: '',
    external_link: '',
  });
  const [selectedFile, setSelectedFile] = useState<DocumentPicker.DocumentPickerAsset | null>(null);
  const [existingFileName, setExistingFileName] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Redirect roles that cannot write evidence at all (strategy_team,
  // org_executive, executive, performance_evaluator, evaluation_approver).
  useEffect(() => {
    if (!canManageEvidence) {
      navigation.goBack();
    }
  }, [canManageEvidence, navigation]);

  // Load existing evidence in edit mode
  useEffect(() => {
    if (!isEditMode) return;
    (async () => {
      try {
        const ev = await evidenceApi.get(evidenceId!);
        setForm({
          title: ev.title || '',
          description: ev.description || '',
          target_criterion: ev.target_criterion || '',
          impact: ev.impact || '',
          external_link: ev.external_link || '',
        });
        if (ev.file) {
          setExistingFileName(String(ev.file).split('/').pop() || '');
        }
      } catch {
        showError(t('evidence.loadFailed'));
      } finally {
        setInitialLoading(false);
      }
    })();
  }, [evidenceId, isEditMode]);

  const setField = (k: string, v: string) =>
    setForm(prev => ({ ...prev, [k]: v }));

  const pickFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'image/*',
          'application/pdf',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ],
        copyToCacheDirectory: true,
      });
      if (!result.canceled && result.assets?.length) {
        setSelectedFile(result.assets[0]);
        setErrors(prev => ({ ...prev, file: '' }));
      }
    } catch {
      showError(t('evidence.createFailed'));
    }
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.title.trim()) e.title = t('validation.required');
    if (!form.description.trim()) e.description = t('validation.required');
    if (!form.target_criterion.trim()) e.target_criterion = t('validation.required');
    if (!form.impact.trim()) e.impact = t('validation.required');
    const hasFile = !!selectedFile || !!existingFileName;
    if (!hasFile && !form.external_link.trim()) {
      e.file = t('evidence.fileOrLinkRequired');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildEvidenceType = (): string => {
    if (selectedFile) {
      const ext = (selectedFile.name || '').split('.').pop()?.toLowerCase() || '';
      return IMAGE_EXT.includes(ext) ? 'image' : 'file';
    }
    if (form.external_link.trim()) return 'link';
    return existingFileName ? 'file' : 'link';
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('related_type', relatedType);
      fd.append('related_id', String(relatedId));
      fd.append('evidence_type', buildEvidenceType());
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('target_criterion', form.target_criterion);
      fd.append('impact', form.impact);
      if (form.external_link.trim()) {
        fd.append('external_link', form.external_link.trim());
      }
      if (selectedFile) {
        if (Platform.OS === 'web') {
          const resp = await fetch(selectedFile.uri);
          const blob = await resp.blob();
          fd.append('file', blob, selectedFile.name);
        } else {
          fd.append('file', {
            uri: selectedFile.uri,
            type: selectedFile.mimeType || 'application/octet-stream',
            name: selectedFile.name || 'file',
          } as any);
        }
      }
      if (isEditMode) {
        await evidenceApi.update(evidenceId!, fd);
        showSuccess(t('evidence.updateSuccess'));
      } else {
        await evidenceApi.create(fd);
        showSuccess(t('evidence.createSuccess'));
      }
      navigation.goBack();
    } catch (err: any) {
      showError(
        err?.response?.data?.detail ||
          err?.response?.data?.file?.[0] ||
          t('evidence.createFailed'),
      );
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loading}>
          <ActivityIndicator size="large" color={COLORS.primary} />
        </View>
      </PageContainer>
    );
  }

  const labeled = (key: keyof typeof form, labelKey: string, multiline = false) => (
    <View style={styles.field}>
      <View style={styles.labelRow}>
        <Text style={styles.requiredStar}>*</Text>
        <Text style={[styles.label, { color: colors.textPrimary }]}>{t(labelKey)}</Text>
      </View>
      <TextInput
        style={[
          styles.input,
          multiline && styles.textArea,
          { backgroundColor: colors.card, borderColor: colors.cardBorder, color: colors.textPrimary },
          errors[key] ? styles.inputError : undefined,
        ]}
        value={form[key]}
        onChangeText={v => setField(key, v)}
        textAlign={isRTL ? 'right' : 'left'}
        multiline={multiline}
        numberOfLines={multiline ? 3 : 1}
      />
      {errors[key] ? <Text style={styles.errorText}>{errors[key]}</Text> : null}
    </View>
  );

  return (
    <PageContainer>
      <Navbar />
      <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            ...(contextLabel ? [{ label: contextLabel }] : []),
            { label: isEditMode ? t('evidence.editEvidence') : t('evidence.addEvidence') },
          ]}
        />

        <View
          style={[styles.mainCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}
        >
          <Text style={[styles.pageTitle, { color: colors.textPrimary }]}>
            {t('evidence.formTitle')}
          </Text>

          {/* Basic Information Section */}
          <View
            style={[styles.section, { backgroundColor: colors.background, borderColor: colors.cardBorder }]}
          >
            <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>
              {t('evidence.basicInfo')}
            </Text>
            {labeled('title', 'evidence.name')}
            {labeled('description', 'evidence.description', true)}
            {labeled('target_criterion', 'evidence.targetCriterion', true)}
            {labeled('impact', 'evidence.impact', true)}
          </View>

          {/* Attachments Section */}
          <View
            style={[styles.section, { backgroundColor: colors.background, borderColor: colors.cardBorder }]}
          >
            <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>
              {t('evidence.attachments')}
            </Text>

            {/* File picker */}
            <View style={styles.field}>
              <Text style={[styles.label, { color: colors.textPrimary }]}>
                {t('evidence.file')}
              </Text>
              <View
                style={[styles.fileRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}
              >
                <TouchableOpacity
                  style={[
                    styles.uploadButton,
                    { backgroundColor: colors.card, borderColor: colors.cardBorder },
                  ]}
                  onPress={pickFile}
                >
                  <Text style={[styles.uploadButtonText, { color: colors.textPrimary }]}>
                    {t('evidence.selectFile')}
                  </Text>
                </TouchableOpacity>
                <View
                  style={[
                    styles.fileNameBox,
                    { backgroundColor: colors.card, borderColor: colors.cardBorder },
                  ]}
                >
                  <Text style={[styles.fileNameText, { color: colors.textMuted }]}>
                    {selectedFile?.name || existingFileName || t('evidence.noFileSelected')}
                  </Text>
                </View>
              </View>
              <View
                style={[
                  styles.uploadInfo,
                  { flexDirection: isRTL ? 'row-reverse' : 'row' },
                ]}
              >
                <Text style={styles.uploadInfoText}>{t('evidence.maxSizeNote')}</Text>
                <InfoIcon size={14} color={colors.textMuted} />
              </View>
            </View>

            {/* Optional external link */}
            <View style={styles.field}>
              <Text style={[styles.label, { color: colors.textPrimary }]}>
                {t('evidence.link')}
              </Text>
              <TextInput
                style={[
                  styles.input,
                  { backgroundColor: colors.card, borderColor: colors.cardBorder, color: colors.textPrimary },
                ]}
                value={form.external_link}
                onChangeText={v => setField('external_link', v)}
                textAlign={isRTL ? 'right' : 'left'}
                placeholder="https://..."
                placeholderTextColor={colors.textMuted}
              />
            </View>

            {errors.file ? <Text style={styles.errorText}>{errors.file}</Text> : null}
          </View>

          {/* Action buttons */}
          <View style={styles.actions}>
            <Button variant="primary" onPress={handleSave} loading={loading}>
              {t('common.save')}
            </Button>
            <Button variant="secondary" onPress={() => navigation.goBack()} disabled={loading}>
              {t('common.cancel')}
            </Button>
          </View>
        </View>
      </ScrollView>
    </PageContainer>
  );
}

// ============================================================
// Styles
// ============================================================

const getStyles = (isRTL: boolean) =>
  StyleSheet.create({
    scroll: {
      flex: 1,
    },
    content: {
      padding: tokens.spacing.absolute['6'],
    },
    loading: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    mainCard: {
      borderWidth: tokens.spacing.absolute['px'],
      borderRadius: tokens.radii.absolute['radius-12'],
      padding: tokens.spacing.absolute['6'],
    },
    pageTitle: {
      fontFamily: FONTS.medium,
      fontSize: tokens.typography.paragraph['paragraph subheading font size'],
      textAlign: getTextAlign(isRTL),
      marginBottom: tokens.spacing.absolute['6'],
    },
    section: {
      borderWidth: tokens.spacing.absolute['px'],
      borderRadius: tokens.radii.absolute['radius-8'],
      padding: tokens.spacing.absolute['4'],
      marginBottom: tokens.spacing.absolute['4'],
    },
    sectionTitle: {
      fontFamily: FONTS.medium,
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      textAlign: getTextAlign(isRTL),
      marginBottom: tokens.spacing.absolute['4'],
    },
    field: {
      marginBottom: tokens.spacing.absolute['4'],
    },
    labelRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: tokens.spacing.absolute['1'],
      marginBottom: tokens.spacing.absolute['2'],
    },
    label: {
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      fontFamily: FONTS.regular,
      textAlign: getTextAlign(isRTL),
      marginBottom: tokens.spacing.absolute['2'],
    },
    requiredStar: {
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      color: tokens.colors.raw.red['500'],
    },
    input: {
      borderWidth: tokens.spacing.absolute['px'],
      borderRadius: tokens.radii.absolute['radius-8'],
      paddingHorizontal: tokens.spacing.absolute['3'],
      paddingVertical: tokens.spacing.absolute['2_5'],
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      fontFamily: FONTS.regular,
      textAlign: getTextAlign(isRTL),
    },
    textArea: {
      minHeight: 80,
      textAlignVertical: 'top',
    },
    inputError: {
      borderColor: tokens.colors.raw.red['500'],
    },
    errorText: {
      color: tokens.colors.raw.red['500'],
      fontSize: tokens.typography.paragraph['paragraph mini font size'],
      fontFamily: FONTS.regular,
      marginTop: tokens.spacing.absolute['1'],
      textAlign: getTextAlign(isRTL),
    },
    fileRow: {
      alignItems: 'center',
      gap: tokens.spacing.absolute['3'],
    },
    uploadButton: {
      borderWidth: tokens.spacing.absolute['px'],
      borderRadius: tokens.radii.absolute['radius-8'],
      paddingHorizontal: tokens.spacing.absolute['4'],
      paddingVertical: tokens.spacing.absolute['2_5'],
      minHeight: 44,
      justifyContent: 'center',
    },
    uploadButtonText: {
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      fontFamily: FONTS.medium,
    },
    fileNameBox: {
      flex: 1,
      borderWidth: tokens.spacing.absolute['px'],
      borderRadius: tokens.radii.absolute['radius-8'],
      paddingHorizontal: tokens.spacing.absolute['3'],
      paddingVertical: tokens.spacing.absolute['2_5'],
      minHeight: 44,
      justifyContent: 'center',
    },
    fileNameText: {
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      fontFamily: FONTS.regular,
      textAlign: getTextAlign(isRTL),
    },
    uploadInfo: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: tokens.spacing.absolute['1_5'],
      marginTop: tokens.spacing.absolute['2'],
    },
    uploadInfoText: {
      fontSize: tokens.typography.paragraph['paragraph mini font size'],
      fontFamily: FONTS.regular,
      color: tokens.colors.raw.gray['400'],
    },
    actions: {
      flexDirection: 'row',
      gap: tokens.spacing.absolute['3'],
      marginTop: tokens.spacing.absolute['2'],
    },
  });
